import type { OptionSelectOptionsField } from '@api/types.gen'
import type { OptionMultiSelectFields } from '../OptionMultiSelect/definition'
import type { OptionSingleSelectFields } from '../OptionSingleSelect/definition'

export enum OnSelectBehavior {
  NONE = 'none',
  SELECT_ALL = 'select_all',
  DESELECT_ALL = 'deselect_all',
}

export enum Position {
  LEFT = 'left',
  RIGHT = 'right',
  BOTH = 'both',
}

export enum ImageMode {
  INLINE = 'inline',
  EDGE = 'edge',
  OVERFLOW = 'overflow',
}

export enum SubtextMode {
  REGULAR = 'regular',
  NEW_ROW = 'new_row',
}

/**
 * Superset of both variants' field values (task 013): OptionSelectBase.vue is
 * shared by OptionSingleSelect and OptionMultiSelect, so it sees the union of
 * their two derived field sets — radio colours from one, checkbox colours and
 * the multi-only extras from the other. Both halves are derived, so neither can
 * drift from its own definition.ts. `import type` of a derived alias is erased
 * at compile time, so no definition reaches a built pipe.
 */
export type OptionSelectFields = OptionSingleSelectFields & OptionMultiSelectFields

export function getDefaultOptionSelectFields(
  overrides?: Partial<OptionSelectFields>,
): OptionSelectFields {
  return {
    options: {
      type: 'option_select_options',
      options: [],
    } satisfies OptionSelectOptionsField,
    valueVariable: undefined,
    hasSelectionVariable: undefined,
    maxSelections: undefined,
    showIndicator: true,
    indicatorPosition: Position.LEFT,
    showImage: false,
    imagePosition: Position.LEFT,
    imageMode: ImageMode.INLINE,
    imageOverflowOffset: undefined,
    doNotMirrorOptionImageOnRtl: false,
    doNotMirrorPadImageOnRtl: false,
    showPadImage: false,
    padImage: undefined,
    imageSkeletonBgColor: undefined,
    imageSkeletonShimmerColor: undefined,
    bgColor: undefined,
    activeBgColor: undefined,
    hoverBgColor: undefined,
    borderColor: undefined,
    activeBorderColor: undefined,
    textColor: undefined,
    textHoverColor: undefined,
    textActiveColor: undefined,
    textFontSize: undefined,
    textFontWeight: undefined,
    textLineHeight: undefined,
    subtextColor: undefined,
    subtextHoverColor: undefined,
    subtextActiveColor: undefined,
    subtextFontSize: undefined,
    subtextFontWeight: undefined,
    subtextLineHeight: undefined,
    subtextGap: undefined,
    subtextMode: undefined,
    isCollapsible: undefined,
    radioBgIdle: undefined,
    radioBgHover: undefined,
    radioBgSelected: undefined,
    radioBorderIdle: undefined,
    radioBorderHover: undefined,
    radioBorderSelected: undefined,
    radioFillIdle: undefined,
    radioFillHover: undefined,
    radioFillSelected: undefined,
    checkboxBgIdle: undefined,
    checkboxBgHover: undefined,
    checkboxBgSelected: undefined,
    checkboxBorderIdle: undefined,
    checkboxBorderHover: undefined,
    checkboxBorderSelected: undefined,
    checkboxFillIdle: undefined,
    checkboxFillHover: undefined,
    checkboxFillSelected: undefined,
    gap: undefined,
    gapColumns: undefined,
    paddingY: undefined,
    paddingX: undefined,
    minHeight: undefined,
    maxWidth: undefined,
    borderRadius: undefined,
    margin: undefined,
    ...overrides,
  }
}
